import {
  CLAIM_STATUSES,
  PAYMENT_STATUSES,
  RESIDENT_LIBERTY_ROLES,
} from '../constants/residentConstants'

const NON_LIBERTY_COLLAPSED_CLAIM_STATUSES = ['EXTERNAL_REVIEW', 'PAYMENT_PENDING', 'RE_OPENED'] as const

/**
 * Reimbursement status label in the resident header / review blocks.
 * Mirrors Angular `resident-edit-new.component.html` claim status display:
 * non-liberty roles see "Liberty Review" for EXTERNAL_REVIEW / PAYMENT_PENDING / RE_OPENED.
 */
export function formatResidentClaimHeaderLabel(roleName: string, claimStatus?: string | null): string {
  const cs = String(claimStatus || '')
  const isLiberty = (RESIDENT_LIBERTY_ROLES as readonly string[]).includes(roleName)
  if (
    !isLiberty &&
    NON_LIBERTY_COLLAPSED_CLAIM_STATUSES.includes(cs as (typeof NON_LIBERTY_COLLAPSED_CLAIM_STATUSES)[number])
  ) {
    return 'Liberty Review'
  }
  const mapped = (CLAIM_STATUSES as Record<string, string>)[cs]
  return mapped || 'Not Started'
}

/**
 * Payment status in the resident header third column (Angular `payment_history` / review log rules).
 * Accepts Resident or Reimbursements detail shapes (same history/log fields).
 */
export function resolveResidentHeaderPaymentStatus(
  detail: {
    payment_history?: Array<Record<string, unknown>> | null
    payment_review_log?: Array<Record<string, unknown>> | null
    resident_default?: { claim_status?: string | null } | null
  } | null,
): string {
  if (!detail) return ''

  const paymentHistory = detail.payment_history ?? []
  const reviewLogs = detail.payment_review_log ?? []
  const statusMap = PAYMENT_STATUSES as Record<string, string>

  if (paymentHistory.length > 0) {
    const lastRow = paymentHistory[paymentHistory.length - 1] as Record<string, unknown>
    const decision = String(lastRow.review_decision ?? '').trim()
    if (decision === 'L1 Recalculated' || decision === 'L2 Recalculated') return ''
    const raw = String(lastRow.payment_status ?? '').trim()
    return statusMap[raw] || raw
  }

  if (!reviewLogs.length) {
    const cs = String(detail.resident_default?.claim_status || '')
    if (['DENIED', 'UNDER_REVIEW', 'RE_OPENED'].includes(cs)) return 'No Benefits Available'
    return ''
  }

  const last = reviewLogs[reviewLogs.length - 1] as Record<string, unknown>
  if (String(last.claim_status) === 'claim_denied') return 'No Benefits Available'
  return ''
}

/**
 * Angular NRLSD: `residentDetail?.new_resident_lease_start_date` (top-level),
 * with fallbacks for nested API shapes used by React.
 */
export function resolveNrlsdDate(detail: {
  new_resident_lease_start_date?: unknown
  resident_default?: { new_resident_lease_start_date?: unknown } | null | object
  unit_key?: Record<string, unknown> | null
} | null): string {
  if (!detail) return ''
  const top = detail.new_resident_lease_start_date
  if (top !== undefined && top !== null && String(top).trim() !== '') return String(top)
  const fromDefault = (detail.resident_default as { new_resident_lease_start_date?: unknown } | null | undefined)
    ?.new_resident_lease_start_date
  if (fromDefault !== undefined && fromDefault !== null && String(fromDefault).trim() !== '') {
    return String(fromDefault)
  }
  const fromUnit = detail.unit_key?.new_resident_lease_start_date
  if (fromUnit !== undefined && fromUnit !== null && String(fromUnit).trim() !== '') {
    return String(fromUnit)
  }
  return ''
}

/**
 * Angular reimbursement summary column visibility:
 * SKIPPED/EVICTION OR (ACTIVE/CLOSED && isCancelClaim).
 */
export function shouldShowReimbursementHeaderSummary(
  rentalStatus?: string | null,
  isCancelClaim = false,
): boolean {
  const status = String(rentalStatus || '')
  if (['SKIPPED', 'EVICTION'].includes(status)) return true
  if (['ACTIVE', 'CLOSED'].includes(status) && isCancelClaim) return true
  return false
}

/**
 * LEASE & APPROVAL card fields:
 * - Lease End: hidden for company/property roles (Angular `companyproperty_roleslist`)
 * - NRLSD: only when reimbursement summary is visible
 * - Application Approver: Liberty roles only
 *
 * Hide the whole card when no fields would render (e.g. Leasing Consultant without Skip/Eviction).
 */
export function shouldShowLeaseApprovalHeaderCard(options: {
  isCompanyRole: boolean
  isLibertyRole: boolean
  showReimbursementSummary: boolean
}): boolean {
  const showLeaseEnd = !options.isCompanyRole
  const showNrlsd = options.showReimbursementSummary
  const showApprover = options.isLibertyRole
  return showLeaseEnd || showNrlsd || showApprover
}

/** Prefer form/API `rental_status_date` like Angular; fall back to claim_request_date. */
export function resolveReimbursementRequestDate(residentDefault?: {
  rental_status_date?: unknown
  claim_request_date?: unknown
} | null): string {
  if (!residentDefault) return ''
  const primary = residentDefault.rental_status_date
  if (primary !== undefined && primary !== null && String(primary).trim() !== '') {
    return String(primary)
  }
  const fallback = residentDefault.claim_request_date
  if (fallback !== undefined && fallback !== null && String(fallback).trim() !== '') {
    return String(fallback)
  }
  return ''
}
